import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "../App.css";
import useFocusTrap from "../hooks/useFocusTrap";

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

async function getCroppedImg(imageSrc, cropPixels) {
  const image = await createImage(imageSrc);
  const size = Math.max(cropPixels.width, cropPixels.height);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // Draw the cropped square
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );

  // Mask into a circle
  ctx.globalCompositeOperation = "destination-in";
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.fill();

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      const file = new File([blob], `avatar-${Date.now()}.png`, { type: "image/png" });
      const previewUrl = URL.createObjectURL(blob);
      resolve({ file, previewUrl });
    }, "image/png");
  });
}

export default function CropperModal({ src, onApply, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useFocusTrap({
    isOpen: true,
    containerRef: modalRef,
    onClose,
    initialFocusRef: closeButtonRef,
    lockScroll: true,
  });

  const onCropComplete = useCallback((_croppedArea, nextCroppedAreaPixels) => {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const result = await getCroppedImg(src, croppedAreaPixels);
      onApply(result);
    } catch (err) {
      // ignore crop failures
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" style={{ zIndex: 250 }}>
      <div
        ref={modalRef}
        className="modal cropping-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cropper-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 id="cropper-modal-title">Edit Image</h3>
          <button ref={closeButtonRef} className="icon-btn" type="button" onClick={onClose} aria-label="Close editor">
            x
          </button>
        </div>
        <div className="cropper-container">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="cropper-controls">
          <label htmlFor="profile-cropper-zoom">Zoom</label>
          <input
            id="profile-cropper-zoom"
            type="range"
            min={1}
            max={3}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
        <div className="button-row" style={{ justifyContent: "flex-end" }}>
          <button className="ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="primary dark" type="button" onClick={handleApply} disabled={saving}>
            {saving ? "Applying..." : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}
